import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import Carousel from "../components/carousel/Carousel";
import { Box, Typography } from "@mui/material";
import { useSession } from "../context/SessionContext";
import { useAssetApi } from "../api/useAssetApi";
import { stripUnitSuffix } from "../utils/systemCode";
import { getBgrCarouselImages } from "../utils/assetImages";
import { resolveCameraDirection } from "../utils/assetCameraDirection";

const IFrame = () => {
  const { systemCode, assetId, image_type } = useParams<{ systemCode: string; assetId: string; image_type?: string }>();
  const normalizedSystemCode = stripUnitSuffix(systemCode || "");
  const { setIsLoading, systems } = useSession();
  const { getAssetsApi } = useAssetApi();
  const [images, setImages] = useState<any[]>([]);
  const [cameraDirection, setCameraDirection] = useState<"cw" | "ccw" | undefined>(undefined);

  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const iframeViewType = useMemo(() => {
    if (!image_type || image_type === "all") {
      return undefined;
    }

    const requestedTypes = Array.from(new Set(image_type.split("-").filter(Boolean)));
    if (requestedTypes.length !== 1) {
      return undefined;
    }

    if (requestedTypes[0] === "uc") {
      return "uc";
    }

    if (requestedTypes[0] === "pano" || requestedTypes[0] === "bgrpano") {
      return "pano";
    }

    return undefined;
  }, [image_type]);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);



  useEffect(() => {
    if (!systemCode || !assetId) return;

    const fetchAssetData = async () => {
      try {
        const body = {
          page: 1,
          per_page: 1,
          system_code: normalizedSystemCode,
          asset_id: assetId,
          isIframe: true,
          ...(iframeViewType ? { iframeViewType } : {}),
        };

        const { assets, aliasResolution } = await getAssetsApi(body)
        const asset = assets[0]
        const bgrImages = getBgrCarouselImages(asset);
        setCameraDirection(resolveCameraDirection(asset, systems));

        const imageSets: Record<string, any[]> = {
          ext: asset.imageExterior || [],
          pano: asset.imagePano || [],
          man: asset.imageManual || [],
          crop: asset.imageCrop || [],
          feat: asset.imageFeatured || [],
          uc: asset.imageUndercarriage || [],
          bgrpano: asset.imageBgrPano || [],
          bgr: bgrImages,
        };

        // URL image_type wins; alias imageSet is the fallback when the URL has none.
        const effectiveImageSet = image_type || aliasResolution?.imageSet || undefined;

        let finalImages: any[] = [];

        if (!effectiveImageSet || effectiveImageSet === "all") {
          finalImages = Object.values(imageSets)
            .flat()
            .filter((image, index, allImages) => (
              allImages.findIndex((candidate) => candidate?.imageUrlCf === image?.imageUrlCf) === index
            ));
        } else {
          const types = effectiveImageSet.split("-");
          for (const type of types) {
            if (imageSets[type]) {
              finalImages = [...finalImages, ...imageSets[type]];
            }
          }
        }

        setImages(finalImages);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching asset data:", error);
        setIsLoading(false);
      }
    };

    fetchAssetData();
  }, [normalizedSystemCode, assetId, image_type, iframeViewType, getAssetsApi, setIsLoading, systems]);

  if (!systemCode || !assetId) return <></>;

  if (images.length === 0) {
    return (
      <Typography variant="h6" data-cy="iframe-no-images">
        No images available for this category
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        width: `${windowSize.width}px`,
        height: `${windowSize.height}px`,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      data-cy="iframe-carousel-loaded"
      data-cy-image-count={images.length}
    >
      <Carousel assetId={assetId} images={images} cameraDirection={cameraDirection} />
    </Box>

  );
};

export default IFrame;
